import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, Icon, SimulatedTag, StatusPill, type Status } from '@hive/ui';
import { api, type VenueAdminItem } from '../api/client';
import { useLoad } from '../api/hooks';
import { Shell } from '../components/Shell';
import { AddVenueDialog, ImportVenuesDialog } from '../components/VenueDialogs';
import { dateShort } from '../format';

type Filter = 'real' | 'all' | 'attention' | 'inactive';

const LICENCE: Record<VenueAdminItem['licenceStatus'], { label: string; status: Status }> = {
  none: { label: 'No licence', status: 'offline' },
  scheduled: { label: 'Starts later', status: 'idle' },
  active: { label: 'Active', status: 'healthy' },
  expiring: { label: 'Expiring', status: 'expiring' },
  expired: { label: 'Expired', status: 'offline' },
};

const needsAttention = (v: VenueAdminItem) =>
  v.active && (v.licenceStatus !== 'active' || v.rightsActive === 0 || v.screens === 0 || v.pairedScreens < v.screens);

/** Every venue in the tenant, for setting up venues, screens, licences and rights. */
export function VenuesPage() {
  const navigate = useNavigate();
  const venues = useLoad(async () => (await api.GET('/v1/setup/venues')).data);
  const [filter, setFilter] = useState<Filter>('real');
  const [query, setQuery] = useState('');
  const [dialog, setDialog] = useState<'add' | 'import' | null>(null);

  const all = venues.data ?? [];
  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return all.filter((v) => {
      if (filter === 'real' && (v.source === 'simulated' || !v.active)) return false;
      if (filter === 'attention' && !needsAttention(v)) return false;
      if (filter === 'inactive' && v.active) return false;
      return !q || [v.name, v.code, v.externalRef, v.city, v.country].some((x) => x?.toLowerCase().includes(q));
    });
  }, [all, filter, query]);
  const count = (f: Filter) =>
    all.filter((v) => (f === 'real' ? v.source !== 'simulated' && v.active : f === 'attention' ? needsAttention(v) : f === 'inactive' ? !v.active : true)).length;

  return (
    <Shell network={null} live>
      <main className="mc-page">
        <div className="mc-pagehead">
          <div className="mc-pagehead__left">
            <h1 className="mc-h1">Venues</h1>
            <span className="mc-pill-soft">{venues.data ? `${all.length} in the tenant` : '…'}</span>
          </div>
          <div className="mc-pagehead__actions">
            <Button onClick={() => setDialog('import')}>Import CSV</Button>
            <Button variant="primary" onClick={() => setDialog('add')}>
              <Icon name="plus" size={18} />
              Add venue
            </Button>
          </div>
        </div>

        <Card className="mc-table-card">
          <div className="mc-toolbar">
            <div className="mc-tabs" role="tablist" aria-label="Filter venues">
              {(
                [
                  ['real', 'Real venues'],
                  ['attention', 'Needs attention'],
                  ['all', 'All, with simulated'],
                  ['inactive', 'Inactive'],
                ] as [Filter, string][]
              ).map(([key, label]) => (
                <button key={key} type="button" role="tab" aria-selected={filter === key} className={`mc-tab${filter === key ? ' mc-tab--on' : ''}`} onClick={() => setFilter(key)}>
                  {label}
                  <span className="mc-tab__count">{venues.data ? count(key) : ''}</span>
                </button>
              ))}
            </div>
            <label className="mc-search__box">
              <Icon name="search" size={16} />
              <span className="hv-sr-only">Find a venue</span>
              <input type="search" placeholder="Find by name, venue ID, CRM reference or city" value={query} onChange={(e) => setQuery(e.target.value)} />
            </label>
          </div>
          {venues.error && <p className="mc-note mc-note--bad">Could not load venues. Refresh to try again.</p>}
          <table className="mc-table">
            <thead>
              <tr>
                <th scope="col">Venue</th>
                <th scope="col">Location</th>
                <th scope="col">Screens</th>
                <th scope="col">Licence</th>
                <th scope="col">Rights in force</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {list.map((v) => (
                <tr key={v.id} className={v.active ? undefined : 'mc-table__row--muted'}>
                  <td>
                    <Link to={`/venues/${v.id}/setup`} className="mc-table__link">
                      <span className="mc-table__who">
                        <span className="mc-strong">
                          {v.name} {v.source === 'simulated' && <SimulatedTag title="Simulated venue" />}
                        </span>
                        <span className="mc-small hv-muted">{[v.code, v.externalRef && `CRM ${v.externalRef}`].filter(Boolean).join(' · ') || 'No venue ID'}</span>
                      </span>
                    </Link>
                  </td>
                  <td>{[v.city, v.country].filter(Boolean).join(', ') || '—'}</td>
                  <td className={v.screens > 0 && v.pairedScreens < v.screens ? 'mc-warn' : undefined}>
                    {v.screens === 0 ? 'None yet' : `${v.pairedScreens} of ${v.screens} paired`}
                  </td>
                  <td>
                    <StatusPill status={LICENCE[v.licenceStatus].status}>{LICENCE[v.licenceStatus].label}</StatusPill>
                    {v.licenceValidTo && <span className="mc-tiny hv-muted mc-line">to {dateShort(v.licenceValidTo)}</span>}
                  </td>
                  <td className={v.rightsActive === 0 ? 'mc-bad' : undefined}>{v.rightsActive === 0 ? 'None' : v.rightsActive}</td>
                  <td>{v.active ? <StatusPill status="healthy">Active</StatusPill> : <StatusPill status="idle">Inactive</StatusPill>}</td>
                </tr>
              ))}
              {venues.data && list.length === 0 && (
                <tr>
                  <td colSpan={6} className="mc-table__empty">
                    No venues match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Card>
      </main>
      {dialog === 'add' && <AddVenueDialog onClose={() => setDialog(null)} onCreated={(id) => navigate(`/venues/${id}/setup`)} />}
      {dialog === 'import' && <ImportVenuesDialog onClose={() => setDialog(null)} onImported={() => void venues.reload()} />}
    </Shell>
  );
}
